/**
 * Wave 4 stage J (module modes, docs/GAME.md#rulesets-game-modes-and-setup): rulesets and game modes.
 *
 * "Simple plays like the classic genre; Full has every system; the mode decides how you win."
 *
 * A game has a ruleset (Simple / Full / Custom), a game mode (Conquest / Wonder Race / Rising Sea /
 * King of the Hill / Economic) and, separately, free-for-all or teams (`settings.mode`). This file
 * turns them into the effective feature toggles and victory switches. It is pure and deterministic
 * (only core/types): the simulation, the server, the replays and the client all resolve a game's
 * settings the same way.
 *
 * Games made before stage J have no `ruleset` / `winMode`: their stored toggles and victory
 * switches keep working unchanged (replays stay identical); they are shown as Full (or Custom
 * when a system was off) plus the mode that matches their victory switches (`describeSettings`).
 */
import {
  featureOn,
  FULL_FEATURES,
  GameMode,
  MERGED_TOGGLES,
  risingSeaPace,
  RULESET_IDS,
  SIMPLE_FEATURES,
  UnitType,
  WIN_MODE_FEATURES,
  WIN_MODE_IDS,
  worldEventFrequency,
  type FeatureToggle,
  type GameSettings,
  type RisingSeaPace,
  type RulesetId,
  type WinModeId,
  type WorldEventFrequency,
} from "../types";

export { FULL_FEATURES, MERGED_TOGGLES, RULESET_IDS, SIMPLE_FEATURES, WIN_MODE_FEATURES, WIN_MODE_IDS, type RulesetId, type WinModeId };

/** Ruleset of a new game (new players start Simple). */
export const DEFAULT_RULESET: RulesetId = "simple";
/** Game mode of a new game. */
export const DEFAULT_WIN_MODE: WinModeId = "conquest";

/** Every feature toggle, in a fixed order (the order of the Custom panel groups is in the setup UI). */
export const FEATURE_TOGGLES = [
  "strategicTerrain",
  "terrainWarfare",
  "climate",
  "resources",
  "stockpiles",
  "seasons",
  "attackTypes",
  "unrest",
  "mercenaries",
  "bombardment",
  "navalWarfare",
  "nukeEffects",
  "weapons",
  "wonders",
  "passage",
  "diplomacyPlus",
  "allyWar",
] as const satisfies readonly FeatureToggle[];
type MissingToggle = Exclude<FeatureToggle, (typeof FEATURE_TOGGLES)[number]>;
const _exhaustive: [MissingToggle] extends [never] ? true : false = true;
void _exhaustive;

/**
 * The switches of the Custom panel: every toggle except the ones merged into another switch
 * (stage O, module simplify, P2: war requests and passage are part of "Diplomacy plus").
 */
export const SWITCHABLE_TOGGLES: readonly FeatureToggle[] = FEATURE_TOGGLES.filter((f) => MERGED_TOGGLES[f] === undefined);

/** The toggles a switch sets: itself and every toggle merged into it (stage O, module simplify). */
export function togglesOfSwitch(f: FeatureToggle): FeatureToggle[] {
  const main = MERGED_TOGGLES[f] ?? f;
  return [main, ...FEATURE_TOGGLES.filter((g) => MERGED_TOGGLES[g] === main)];
}

/** `s` with switch `f` (and the toggles merged into it) on or off (stage O, module simplify). */
export function withSwitch(s: GameSettings, f: FeatureToggle, on: boolean): GameSettings {
  const out: GameSettings = { ...s };
  for (const g of togglesOfSwitch(f)) out[g] = on;
  return out;
}

/**
 * Units that cannot be built in the Simple ruleset (their systems are off): warships of the naval
 * war (only transport boats and trade ships sail), the air force, the extractor and the MIRV ("the
 * atom and hydrogen bomb" stay). Stage O (module simplify, owner-approved P8): the SAM launcher is
 * allowed, so a Simple player has a counter to the nations' nukes.
 */
export const SIMPLE_DISABLED_UNITS: readonly UnitType[] = [
  UnitType.Warship,
  UnitType.PatrolBoat,
  UnitType.Battleship,
  UnitType.Shipyard,
  UnitType.Airfield,
  UnitType.Extractor,
  UnitType.MIRV,
];

/**
 * The system a unit belongs to: with that system off the unit cannot be built in any ruleset
 * (GameConfig.isUnitDisabled for the extractor and the airfield, canBuild in
 * executions/structures/rules.ts for the warships and the shipyard). Stage M (module simplify).
 */
export const UNIT_SYSTEM: Readonly<Partial<Record<UnitType, FeatureToggle>>> = {
  [UnitType.Warship]: "navalWarfare",
  [UnitType.PatrolBoat]: "navalWarfare",
  [UnitType.Battleship]: "navalWarfare",
  [UnitType.Shipyard]: "navalWarfare",
  [UnitType.Airfield]: "attackTypes",
  [UnitType.Extractor]: "resources",
};

/** The system of unit `u` is on in `s` (always true for units without one). */
export function unitSystemOn(s: GameSettings, u: UnitType): boolean {
  const sys = UNIT_SYSTEM[u];
  return sys === undefined || featureOn(s, sys);
}

/**
 * Simple units that no switched-off Simple system already rules out (the MIRV; stage O allows the
 * SAM launcher). Simple -> Custom puts them on the host's unit list, so Custom starts with the
 * units of Simple too. Stage M (module simplify).
 */
export const SIMPLE_LISTED_UNITS: readonly UnitType[] = SIMPLE_DISABLED_UNITS.filter((u) => {
  const sys = UNIT_SYSTEM[u];
  return sys === undefined || SIMPLE_FEATURES[sys];
});

/** What Simple -> Custom listed before stage O allowed the SAM launcher in Simple (stored setups still carry it). */
const STAGE_M_LISTED_UNITS: readonly UnitType[] = [UnitType.SAMLauncher, UnitType.MIRV];

export function isRulesetId(v: unknown): v is RulesetId {
  return typeof v === "string" && (RULESET_IDS as readonly string[]).includes(v);
}

export function isWinModeId(v: unknown): v is WinModeId {
  return typeof v === "string" && (WIN_MODE_IDS as readonly string[]).includes(v);
}

/** The settings were made before stage J (no ruleset and no game mode stored). */
export function isLegacySettings(s: GameSettings): boolean {
  return s.ruleset === undefined && s.winMode === undefined;
}

/**
 * The ruleset of a game for display, the ladder and the pickers. Settings without one (made
 * before stage J) map to Full, or to Custom when one of their systems or world events is off.
 */
export function rulesetOf(s: GameSettings): RulesetId {
  if (isRulesetId(s.ruleset)) return s.ruleset;
  for (const f of FEATURE_TOGGLES) if (s[f] === false) return "custom";
  if (s.worldEvents !== undefined && s.worldEvents !== "normal") return "custom";
  return "full";
}

/**
 * The game mode of a game. Settings without one (made before stage J) map to the mode of their
 * victory switches: Rising Sea first, then Wonder Race, King of the Hill, Economic; else Conquest.
 */
export function winModeOf(s: GameSettings): WinModeId {
  if (isWinModeId(s.winMode)) return s.winMode;
  const pace = s.risingSea;
  if (pace === "slow" || pace === "normal" || pace === "fast") return "risingSea";
  if (s.wonderVictory === true && s.wonders !== false) return "wonderRace";
  if (s.hillVictory === true) return "kingOfTheHill";
  if (s.economicVictory === true && !s.infiniteGold) return "economic";
  return "conquest";
}

/** The ruleset and game mode of any settings (old ones mapped to Full / Custom + the matching mode). */
export function describeSettings(s: GameSettings): { ruleset: RulesetId; winMode: WinModeId; teams: boolean } {
  return { ruleset: rulesetOf(s), winMode: winModeOf(s), teams: s.mode === GameMode.Teams };
}

/**
 * The effective feature toggles of a game: the ruleset first (Simple = SIMPLE_FEATURES, Full =
 * FULL_FEATURES, Custom / old settings = the stored toggles, absent = on), then the systems the game
 * mode needs (Wonder Race needs wonders). Same answer as featureOn(settings, f) for every f.
 */
export function effectiveToggles(s: GameSettings): Record<FeatureToggle, boolean> {
  const out = {} as Record<FeatureToggle, boolean>;
  for (const f of FEATURE_TOGGLES) out[f] = featureOn(s, f);
  return out;
}

/** Everything the ruleset and the game mode decide, resolved (see effectiveToggles). */
export interface EffectiveRules {
  ruleset: RulesetId;
  winMode: WinModeId;
  teams: boolean;
  /** Settings made before stage J (shown as `ruleset` / `winMode`, played as stored). */
  legacy: boolean;
  features: Record<FeatureToggle, boolean>;
  worldEvents: WorldEventFrequency;
  risingSea: RisingSeaPace;
  wonderVictory: boolean;
  hillVictory: boolean;
  economicVictory: boolean;
  /** Units that cannot be built: the host's list plus the ruleset's (Simple). */
  disabledUnits: UnitType[];
}

/** The victory switches of a game: from its mode, or as stored for settings made before stage J. */
function victorySwitches(s: GameSettings): { wonderVictory: boolean; hillVictory: boolean; economicVictory: boolean } {
  const m = s.winMode;
  if (m === undefined || !isWinModeId(m)) {
    return { wonderVictory: s.wonderVictory === true, hillVictory: s.hillVictory === true, economicVictory: s.economicVictory === true };
  }
  return { wonderVictory: m === "wonderRace", hillVictory: m === "kingOfTheHill", economicVictory: m === "economic" };
}

/** Units the ruleset disables on top of the host's list (in a fixed order, no duplicates). */
export function disabledUnitsOf(s: GameSettings): UnitType[] {
  const out = Array.isArray(s.disabledUnits) ? s.disabledUnits.slice() : [];
  if (s.ruleset === "simple") for (const u of SIMPLE_DISABLED_UNITS) if (!out.includes(u)) out.push(u);
  return out;
}

export function effectiveRules(s: GameSettings): EffectiveRules {
  const d = describeSettings(s);
  return {
    ...d,
    legacy: isLegacySettings(s),
    features: effectiveToggles(s),
    worldEvents: worldEventFrequency(s),
    risingSea: risingSeaPace(s),
    ...victorySwitches(s),
    disabledUnits: disabledUnitsOf(s),
  };
}

/**
 * The settings with every ruleset / mode decision written out explicitly (feature toggles,
 * victory switches, Rising Sea pace, world events, disabled units), so code that reads a field
 * directly sees the effective value. Keeps `ruleset` and `winMode`. Idempotent; settings made
 * before stage J resolve to the same game they always were. Game (normalizeSettings) and the
 * client's GameView apply it.
 */
export function resolveSettings(s: GameSettings): GameSettings {
  const r = effectiveRules(s);
  const out: GameSettings = { ...s, ...r.features, disabledUnits: r.disabledUnits };
  out.worldEvents = r.worldEvents;
  out.risingSea = r.risingSea;
  out.wonderVictory = r.wonderVictory;
  out.hillVictory = r.hillVictory;
  out.economicVictory = r.economicVictory;
  // The Economic mode is a race to bank gold: infinite gold would make it meaningless.
  if (s.winMode === "economic") out.infiniteGold = false;
  return out;
}

/** The Custom switches of `s` show exactly `set` (null = every system on) and the world events are at their default. */
function switchesShow(s: GameSettings, set: Readonly<Record<FeatureToggle, boolean>> | null): boolean {
  const c: GameSettings = { ...s, ruleset: "custom", winMode: undefined };
  for (const f of FEATURE_TOGGLES) if (featureOn(c, f) !== (set === null || set[f])) return false;
  return s.worldEvents === undefined || s.worldEvents === "normal";
}

/**
 * The host never touched the Custom switches: every system on (or absent), or (stage O) exactly
 * the Full set that Full -> Custom copies, and the world events at their default. Stage M
 * (module simplify).
 */
export function customSwitchesUntouched(s: GameSettings): boolean {
  return switchesShow(s, null) || switchesShow(s, FULL_FEATURES);
}

/** The switches still hold the copy of Simple that withRuleset made (the Simple set, no world events). */
function switchesAreSimpleCopy(s: GameSettings): boolean {
  for (const f of FEATURE_TOGGLES) if (s[f] !== SIMPLE_FEATURES[f]) return false;
  return s.worldEvents === "off";
}

/**
 * The setup / lobby pickers. Simple and Full ignore the stored toggles (fixed sets), so they stay
 * untouched: switching back to Custom finds the host's own choices again. Stage M (module
 * simplify): while the host never touched the switches, Custom starts from the rules they were
 * looking at (from Simple: the Simple set, no world events and the Simple units via the host's unit
 * list; from Full: the Full set (stage O: Diplomacy plus off) and every unit), instead of jumping
 * to every system on. Picking Full while the switches still hold that Simple copy takes it back
 * (switches untouched again; Simple's listed units, if all still listed, allowed again), so Full
 * never inherits an untouched copy and a later Custom starts as Full. With any other switches the
 * unit list is the host's and stays (a MIRV switched off in Custom survives a look at Full). The
 * mode replaces the victory switches.
 */
export function withRuleset(s: GameSettings, ruleset: RulesetId): GameSettings {
  const from = rulesetOf(s);
  const listed = Array.isArray(s.disabledUnits) ? s.disabledUnits : [];
  if (ruleset === "full" && from !== "full" && switchesAreSimpleCopy(s)) {
    // Only the picker's own copy goes back: a unit the host listed with other switches (a MIRV
    // switched off in Custom or Full) stays on the list (simplify2 review).
    const out: GameSettings = { ...s, ruleset };
    for (const f of FEATURE_TOGGLES) out[f] = true;
    delete out.worldEvents;
    // A copy made before stage O also listed the SAM launcher: it goes back with the MIRV.
    const copied = STAGE_M_LISTED_UNITS.every((u) => listed.includes(u)) ? STAGE_M_LISTED_UNITS : SIMPLE_LISTED_UNITS;
    if (copied.every((u) => listed.includes(u))) out.disabledUnits = listed.filter((u) => !copied.includes(u));
    return out;
  }
  if (ruleset !== "custom" || from === "custom" || !customSwitchesUntouched(s)) return { ...s, ruleset };
  const out: GameSettings = { ...s, ruleset };
  if (from === "full") {
    // Stage O (simplify, P1): Full has no Diplomacy plus, so Custom from Full starts without it too.
    for (const f of FEATURE_TOGGLES) out[f] = FULL_FEATURES[f];
    return out;
  }
  for (const f of FEATURE_TOGGLES) out[f] = SIMPLE_FEATURES[f];
  out.worldEvents = worldEventFrequency({ ...s, ruleset: "simple" });
  out.disabledUnits = [...listed, ...SIMPLE_LISTED_UNITS.filter((u) => !listed.includes(u))];
  return out;
}

export function withWinMode(s: GameSettings, winMode: WinModeId): GameSettings {
  const out: GameSettings = { ...s, winMode };
  if (winMode === "risingSea" && risingSeaPace({ ...s, winMode: undefined }) === "off") out.risingSea = "normal";
  return out;
}

/** The systems a ruleset switches off (empty for Full; the host's choices for Custom). */
export function systemsOffBy(s: GameSettings): FeatureToggle[] {
  const f = effectiveToggles(s);
  return FEATURE_TOGGLES.filter((k) => !f[k]);
}

/** Systems the game mode turns on although the ruleset has them off ("implies Full rules for them"). */
export function systemsAddedByMode(s: GameSettings): FeatureToggle[] {
  const m = s.winMode;
  if (m === undefined || !isWinModeId(m)) return [];
  const withoutMode = { ...s, winMode: undefined };
  return WIN_MODE_FEATURES[m].filter((f) => !featureOn(withoutMode, f));
}

/**
 * The ladder bucket of a game: ratings are kept per ruleset and game mode ("rated per mode and
 * ruleset"). Custom games are not rated (their rules differ per host); old settings map like
 * describeSettings (Full + the matching mode). Stage O (module simplify, P1): the `full:*` buckets
 * keep their names and ratings, but games rated there since stage O play Full without Diplomacy
 * plus (pacts, tribute, vassals, war requests, passage).
 */
export function ladderBucketOf(s: GameSettings): string | null {
  const d = describeSettings(s);
  if (d.ruleset === "custom") return null;
  return `${d.ruleset}:${d.winMode}`;
}

/** Every rated ladder bucket (ruleset × mode), in picker order. */
export const LADDER_BUCKETS: readonly string[] = (["simple", "full"] as const).flatMap((r) => WIN_MODE_IDS.map((m) => `${r}:${m}`));

/**
 * Settings with their ruleset and game mode stored (old settings get the ones describeSettings
 * maps them to). For remembered setup / lobby choices, never for replays (those play as stored).
 */
export function pinRulesetAndMode(s: GameSettings): GameSettings {
  if (isRulesetId(s.ruleset) && isWinModeId(s.winMode)) return s;
  return { ...s, ruleset: rulesetOf(s), winMode: winModeOf(s) };
}

/** Settings of a brand-new player: the defaults with the Simple ruleset and Conquest. */
export function withNewGameRules(s: GameSettings): GameSettings {
  return { ...s, ruleset: DEFAULT_RULESET, winMode: DEFAULT_WIN_MODE };
}

/** The ladder bucket shown and answered when none is asked for. */
export const DEFAULT_LADDER_BUCKET = "simple:conquest";

/** A known ladder bucket ("<simple|full>:<mode>"). */
export function isLadderBucket(v: unknown): v is string {
  return typeof v === "string" && LADDER_BUCKETS.includes(v);
}
